import {
  getRandomValueFromArray,
  getTimeDifferenceInHours
} from '@nw/shared/utils';

import {
  ApplicationSectionNameEnums,
  NAT_GUIDE_URL
} from '../constants/ApplicationSectionConstants';

export const OFFLINE_EXAM_SUBMISSION_APPLICATION_TYPE_FIELD_ID =
  '895e914f-c3c1-474a-9638-5e8755c9d3ab';

const getCompletionPercentage = (
  section: any,
  sectionCompletionPercentage: number
) => {
  for (let j = 0; j < section.subSection.length; j++) {
    const subSection = section.subSection[j];
    sectionCompletionPercentage += subSection.completionPercentage;
  }
  return sectionCompletionPercentage;
};

const shouldShowResultInProgressScreenForSlotSelection = (
  subSection?: any,
  isOfflineExamSubmission?: boolean
) => {
  const hasExamCompleted =
    subSection?.sectionCompletedAt && subSection.completionPercentage >= 100;

  if (isOfflineExamSubmission && subSection?.completionPercentage === 50) {
    return true;
  } else if (isOfflineExamSubmission && hasExamCompleted) {
    return false;
  } else if (hasExamCompleted && subSection?.firstVisitedDatetime) {
    return false;
  } else {
    const timeDifferenceInHours = hasExamCompleted
      ? getTimeDifferenceInHours(subSection.sectionCompletedAt)
      : null;

    return timeDifferenceInHours !== null && timeDifferenceInHours > 0;
  }
};

const shouldShowQualifiedReportPreviewForSlotSelection = (subSection?: any) => {
  const hasExamCompleted =
    subSection?.sectionCompletedAt && subSection.completionPercentage >= 100;
  if (!hasExamCompleted || subSection?.firstVisitedDatetime) return false;
  const timeDifferenceInHours = getTimeDifferenceInHours(
    subSection.sectionCompletedAt
  );
  return (
    timeDifferenceInHours !== null &&
    timeDifferenceInHours <= 0 &&
    timeDifferenceInHours > -24
  );
};

export const isOfflineExamSubmissionApplication = (
  applicationType?: string,
  templateApplicationType?: string
) =>
  applicationType === 'OFFLINE_EXAM_SUBMISSION' ||
  templateApplicationType === 'OFFLINE_EXAM_SUBMISSION';

/** Direct campus path from personal-details template field (not from section config). */
export const isDirectCampusVisitedApplication = (
  initialValues?: Record<string, unknown> | null
): boolean => initialValues?.IS_DIRECT_CAMPUS_VISITED === 'YES';

/** Seat reservation (take admission) complete without direct campus visit flows — hide non-whitelisted tabs. */
export const getShouldHideSectionOnCompletionOfSR = (
  sectionDetailsInfoTabsConfig: any[]
): boolean => {
  const takeAdmissionSectionInConfig = sectionDetailsInfoTabsConfig.find(
    (s) =>
      s.id === ApplicationSectionNameEnums.TAKE_ADMISSION ||
      s.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  const takeAdmissionCompletionPercentage =
    takeAdmissionSectionInConfig?.completionPercentage ?? 0;
  const hasDirectCampusVisitSection = Boolean(
    sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    )
  );
  const hasDirectCampusVisitSrSection = Boolean(
    sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    )
  );
  return (
    takeAdmissionCompletionPercentage >= 100 &&
    !hasDirectCampusVisitSection &&
    !hasDirectCampusVisitSrSection
  );
};

export const getSectionDetailsDisableInfoTabsConfig = (
  sectionDetailsInfoTabsConfig: any,
  loggedInUserRole?: string,
  isRescheduleExam?: boolean,
  applicationType?: string,
  templateApplicationType?: string,
  isDirectCampusVisitedFromField = false
) => {
  const isOfflineExamSubmission = isOfflineExamSubmissionApplication(
    applicationType,
    templateApplicationType
  );
  const sectionDetailsInfoTabsDetailsArray: any[] = [];

  let selectedSectionTabId = '';
  let selectedSubSectionId = '';
  let selectedSectionCompletionPercentage = 0;

  //maintain completion percentage of each section
  let bookExamSlotCompletionPercentage = 0;
  let campusVisitSchedulerCompletionPercentage = 0;
  let personalDetailsCompletionPercentage = 0;
  let webinarSectionCompletionPercentage = 0;
  let onlineCounsellingCompletionPercentage = 0;
  let bookAdmissionTestFeeCompletionPercentage = 0;
  let natSlotSelectionCompletionPercentage = 0;
  const takeAdmissionSectionInConfig = sectionDetailsInfoTabsConfig.find(
    (s) =>
      s.id === ApplicationSectionNameEnums.TAKE_ADMISSION ||
      s.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  let takeAdmissionCompletionPercentage =
    takeAdmissionSectionInConfig?.completionPercentage ?? 0;
  let closureSessionCompletionPercentage = 0;
  let natOfficeVisitedCompletionPercentage = 0;
  const directCampusVisitSection = sectionDetailsInfoTabsConfig.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
  );
  const hasDirectCampusVisitSection = Boolean(directCampusVisitSection);
  const directCampusVisitSrSection = sectionDetailsInfoTabsConfig.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  const hasDirectCampusVisitSrSection = Boolean(directCampusVisitSrSection);
  const isDirectFlowUser =
    isDirectCampusVisitedFromField || hasDirectCampusVisitSrSection;
  let directCampusVisitCompletionPercentage =
    directCampusVisitSection?.completionPercentage ?? 0;
  let directCampusVisitSrCompletionPercentage =
    directCampusVisitSrSection?.completionPercentage ?? 0;

  const getPrimaryDirectCampusVisitTabIdForSelection = () => {
    if (hasDirectCampusVisitSection) {
      return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
    }
    if (isDirectCampusVisitedFromField && hasDirectCampusVisitSrSection) {
      return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
    }
    return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
  };

  const primaryDirectCampusVisitTabId =
    getPrimaryDirectCampusVisitTabIdForSelection();

  const visibleSectionsOnCompletionOfSR = [
    ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026,
    ApplicationSectionNameEnums.TAKE_ADMISSION,
    ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
  ];
  const postAdmissionHiddenSections = [
    ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  ];

  const applySectionVisibilityOverrides = ({
    visibleSections = [],
    hiddenSections = []
  }: {
    visibleSections?: string[];
    hiddenSections?: string[];
  }) => {
    const visibleSectionsSet = new Set(visibleSections);
    const hiddenSectionsSet = new Set(hiddenSections);
    const hasVisibleOverrides = visibleSectionsSet.size > 0;

    sectionDetailsInfoTabsDetailsArray.forEach((section) => {
      if (hasVisibleOverrides) {
        section.isHidden = !visibleSectionsSet.has(section.id);
        return;
      }

      if (hiddenSectionsSet.has(section.id)) section.isHidden = true;
    });
  };

  for (let i = 0; i < sectionDetailsInfoTabsConfig.length; i++) {
    const section = sectionDetailsInfoTabsConfig[i];

    //handle personal details section
    if (section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS) {
      //this is initial section, so it is not disable
      section.isDisable = false;
      personalDetailsCompletionPercentage = section.completionPercentage;

      let natCompletionPercentage = 0;
      const natSection = sectionDetailsInfoTabsConfig.find(
        (section) => section.id === ApplicationSectionNameEnums.NAT
      );
      natCompletionPercentage = natSection
        ? getCompletionPercentage(natSection, 0)
        : 0;
      natSlotSelectionCompletionPercentage = natCompletionPercentage;

      if (
        (section.completionPercentage < 100 && natCompletionPercentage < 100) ||
        !loggedInUserRole
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    ) {
      bookAdmissionTestFeeCompletionPercentage = section.completionPercentage;

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        hasDirectCampusVisitSection &&
        directCampusVisitCompletionPercentage < 100
      ) {
        section.isDisable = true;
        if (!selectedSectionTabId && !selectedSubSectionId) {
          selectedSectionTabId = primaryDirectCampusVisitTabId;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        }
      } else if (
        hasDirectCampusVisitSrSection &&
        directCampusVisitSrCompletionPercentage < 100
      ) {
        section.isDisable = true;
        if (!selectedSectionTabId && !selectedSubSectionId) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitSrCompletionPercentage;
        }
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;
        if (
          !selectedSectionTabId &&
          !selectedSubSectionId &&
          bookAdmissionTestFeeCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE;
          selectedSectionCompletionPercentage = section.completionPercentage;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    ) {
      directCampusVisitCompletionPercentage = section.completionPercentage ?? 0;

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;

        if (
          !selectedSectionTabId &&
          !selectedSubSectionId &&
          directCampusVisitCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    ) {
      directCampusVisitSrCompletionPercentage =
        section.completionPercentage ?? 0;

      // No DIRECT_CAMPUS_VISIT_2026 in config: show SR only for direct-campus (template) users.
      // Always set both ways — tab config objects are reused across recomputes; leaving isHidden
      // unset would keep stale true after IS_DIRECT_CAMPUS_VISITED loads from template.
      if (!hasDirectCampusVisitSection) {
        section.isHidden = !isDirectCampusVisitedFromField;
      }

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        !(
          !hasDirectCampusVisitSection ||
          directCampusVisitCompletionPercentage >= 100
        )
      ) {
        section.isDisable = true;
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;
      }

      if (
        !section.isHidden &&
        !section.isDisable &&
        directCampusVisitSrCompletionPercentage < 100 &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.BOOK_EXAM_SLOT_AND_PAY
    ) {
      //handle book exam slot and pay section
      for (let j = 0; j < section.subSection.length; j++) {
        const subSection = section.subSection[j];
        bookExamSlotCompletionPercentage += subSection.completionPercentage;

        if (
          !selectedSubSectionId &&
          !selectedSectionTabId &&
          subSection.completionPercentage < 100
        ) {
          selectedSubSectionId = subSection.id;
        }
      }

      bookExamSlotCompletionPercentage = bookExamSlotCompletionPercentage / 2;
      section.completionPercentage = bookExamSlotCompletionPercentage;

      if (
        (bookExamSlotCompletionPercentage > 0 || loggedInUserRole) &&
        personalDetailsCompletionPercentage >= 100
      ) {
        //loggedInUserRole case will be checked when personal details section is 100% but user has not clicked on save and book . so we should not move to payment section on next reload.
        section.isDisable = false;
      } else if (personalDetailsCompletionPercentage < 100) {
        section.isDisable = true;
      }

      if (
        !selectedSectionTabId &&
        bookExamSlotCompletionPercentage < 100 &&
        personalDetailsCompletionPercentage >= 100
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = bookExamSlotCompletionPercentage;
      }
    } else if (section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION) {
      const natSlotSelectionSubSections = section.subSection || [];
      natSlotSelectionCompletionPercentage = getCompletionPercentage(
        section,
        0
      );
      const examSlotDetailsSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
      );
      const topinExamSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.TOPIN_EXAM
      );

      const retakeExamSlotDetailsSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id ===
          ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_DETAILS
      );

      const retakeTopinExamSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
      );

      if (
        bookAdmissionTestFeeCompletionPercentage >= 100 &&
        personalDetailsCompletionPercentage >= 100
      ) {
        section.isDisable = false;
      } else if (
        bookAdmissionTestFeeCompletionPercentage < 100 &&
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = true;
        if (
          hasDirectCampusVisitSection &&
          directCampusVisitCompletionPercentage < 100
        ) {
          selectedSectionTabId = primaryDirectCampusVisitTabId;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        } else if (
          hasDirectCampusVisitSrSection &&
          directCampusVisitSrCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitSrCompletionPercentage;
        } else {
          selectedSectionTabId =
            ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE;
          selectedSectionCompletionPercentage =
            bookAdmissionTestFeeCompletionPercentage;
        }
      } else if (personalDetailsCompletionPercentage < 100) {
        if (
          natSlotSelectionCompletionPercentage /
            natSlotSelectionSubSections.length <=
            100 &&
          ((topinExamSubSection &&
            shouldShowResultInProgressScreenForSlotSelection(
              topinExamSubSection,
              isOfflineExamSubmission
            )) ||
            (retakeTopinExamSubSection &&
              shouldShowResultInProgressScreenForSlotSelection(
                retakeTopinExamSubSection,
                isOfflineExamSubmission
              )))
        ) {
          section.isDisable = true;
          selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        } else if (
          personalDetailsCompletionPercentage < 100 &&
          isOfflineExamSubmission
        ) {
          section.isDisable = true;
          selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        } else if (
          natSlotSelectionCompletionPercentage /
            natSlotSelectionSubSections.length >=
          100
        ) {
          section.isDisable = false;
        } else {
          section.isDisable = true;
          selectedSectionTabId = ApplicationSectionNameEnums.PERSONAL_DETAILS;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        }
      }

      if (
        retakeTopinExamSubSection &&
        retakeTopinExamSubSection.completionPercentage >= 100
      ) {
        if (natSlotSelectionSubSections.length === 1) {
          natSlotSelectionCompletionPercentage = 100;
        } else if (
          shouldShowResultInProgressScreenForSlotSelection(
            retakeTopinExamSubSection,
            isOfflineExamSubmission
          )
        ) {
          section.shouldShowResultInProgressScreen = true;
          natSlotSelectionCompletionPercentage = 50;
        } else if (
          shouldShowQualifiedReportPreviewForSlotSelection(
            retakeTopinExamSubSection
          )
        ) {
          section.shouldShowQualifiedReportPreview = true;
          natSlotSelectionCompletionPercentage = 50;
        } else {
          natSlotSelectionCompletionPercentage =
            natSlotSelectionCompletionPercentage /
            natSlotSelectionSubSections.length;
        }
      } else if (
        topinExamSubSection &&
        topinExamSubSection.completionPercentage >= 100 &&
        natSlotSelectionSubSections.length === 1
      ) {
        natSlotSelectionCompletionPercentage = 100;
      } else if (
        topinExamSubSection &&
        shouldShowResultInProgressScreenForSlotSelection(
          topinExamSubSection,
          isOfflineExamSubmission
        )
      ) {
        section.shouldShowResultInProgressScreen = true;
        natSlotSelectionCompletionPercentage = 50;
      } else if (
        topinExamSubSection &&
        shouldShowQualifiedReportPreviewForSlotSelection(topinExamSubSection)
      ) {
        section.shouldShowQualifiedReportPreview = true;
        natSlotSelectionCompletionPercentage = 50;
      } else {
        natSlotSelectionCompletionPercentage =
          natSlotSelectionCompletionPercentage /
          natSlotSelectionSubSections.length;
      }
      section.completionPercentage = natSlotSelectionCompletionPercentage;

      const shouldSelectNatSlotSelection =
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        section.completionPercentage < 100;

      if (shouldSelectNatSlotSelection) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;

        // If retake exam slot details is 100% complete and retake topin exam exists, select retake topin exam
        if (
          retakeExamSlotDetailsSubSection &&
          retakeExamSlotDetailsSubSection.completionPercentage >= 100 &&
          retakeTopinExamSubSection
        ) {
          selectedSubSectionId = retakeTopinExamSubSection.id;
        }
        // If exam slot details is 100% complete and retake exam slot exists (but not 100%), select retake exam slot
        else if (
          examSlotDetailsSubSection &&
          examSlotDetailsSubSection.completionPercentage >= 100 &&
          retakeExamSlotDetailsSubSection &&
          retakeExamSlotDetailsSubSection.completionPercentage < 100
        ) {
          selectedSubSectionId = retakeExamSlotDetailsSubSection.id;
        }
        // If exam slot details is 100% complete and topin exam exists (initial structure), select topin exam
        else if (
          examSlotDetailsSubSection &&
          examSlotDetailsSubSection.completionPercentage >= 100 &&
          topinExamSubSection
        ) {
          selectedSubSectionId = topinExamSubSection.id;
        }
        // If exam slot details exists, select it
        else if (examSlotDetailsSubSection) {
          selectedSubSectionId = examSlotDetailsSubSection.id;
        }
        // Fallback to first subsection
        else if (natSlotSelectionSubSections.length > 0) {
          selectedSubSectionId = natSlotSelectionSubSections[0].id;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
    ) {
      campusVisitSchedulerCompletionPercentage = getCompletionPercentage(
        section,
        0
      );

      campusVisitSchedulerCompletionPercentage =
        campusVisitSchedulerCompletionPercentage / section.subSection.length;

      section.completionPercentage = campusVisitSchedulerCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else if (natSlotSelectionCompletionPercentage >= 100) {
        section.isDisable = false;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        campusVisitSchedulerCompletionPercentage < 100
      ) {
        if (campusVisitSchedulerCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER;
          selectedSectionCompletionPercentage =
            campusVisitSchedulerCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.NAT;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.WEBINAR) {
      webinarSectionCompletionPercentage =
        getCompletionPercentage(section, 0) / section.subSection.length;

      section.completionPercentage = webinarSectionCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else if (natSlotSelectionCompletionPercentage >= 100) {
        section.isDisable = false;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        webinarSectionCompletionPercentage < 100
      ) {
        if (webinarSectionCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.WEBINAR_REGISTRATION;
          selectedSectionCompletionPercentage =
            webinarSectionCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.NAT;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.ONLINE_COUNSELLING) {
      onlineCounsellingCompletionPercentage = getCompletionPercentage(
        section,
        0
      );
      onlineCounsellingCompletionPercentage =
        onlineCounsellingCompletionPercentage / section.subSection.length;

      section.completionPercentage = onlineCounsellingCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else if (
        webinarSectionCompletionPercentage >= 100 ||
        section.completionPercentage >= 100
      ) {
        section.isDisable = false;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        onlineCounsellingCompletionPercentage < 100
      ) {
        if (onlineCounsellingCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.BOOK_A_PRE_COUNSELLING_SESSION;
          selectedSectionCompletionPercentage =
            onlineCounsellingCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.WEBINAR;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.CLOSURE_SESSION_REGISTRATION
    ) {
      closureSessionCompletionPercentage = section.completionPercentage ?? 0;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else if (onlineCounsellingCompletionPercentage >= 100) {
        section.isDisable = false;
      } else {
        section.isDisable = true;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        closureSessionCompletionPercentage < 100
      ) {
        if (closureSessionCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSectionCompletionPercentage =
            closureSessionCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.ONLINE_COUNSELLING;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.NAT_OFFICE_VISITED) {
      natOfficeVisitedCompletionPercentage = section.completionPercentage ?? 0;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else if (
        onlineCounsellingCompletionPercentage >= 100 ||
        campusVisitSchedulerCompletionPercentage >= 100
      ) {
        section.isDisable = false;
      } else {
        section.isDisable = true;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        natOfficeVisitedCompletionPercentage < 100
      ) {
        if (natOfficeVisitedCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSectionCompletionPercentage =
            natOfficeVisitedCompletionPercentage;
        } else {
          selectedSectionTabId =
            campusVisitSchedulerCompletionPercentage >= 100
              ? ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
              : ApplicationSectionNameEnums.ONLINE_COUNSELLING;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.TAKE_ADMISSION) {
      takeAdmissionCompletionPercentage = section.completionPercentage;
      const isDirectCampusPathClearForTakeAdmission =
        hasDirectCampusVisitSection
          ? directCampusVisitCompletionPercentage >= 100
          : isDirectCampusVisitedFromField
            ? directCampusVisitSrCompletionPercentage >= 100
            : directCampusVisitCompletionPercentage >= 100;
      if (
        natSlotSelectionCompletionPercentage < 100 &&
        section.completionPercentage < 100
      ) {
        section.isHidden = true;
      } else if (
        isDirectCampusPathClearForTakeAdmission ||
        natOfficeVisitedCompletionPercentage >= 100 ||
        closureSessionCompletionPercentage >= 100 ||
        section.completionPercentage >= 100
      ) {
        section.isHidden = false;
        section.isDisable = false;
      } else {
        section.isDisable = true;
      }
      if (
        !section.isHidden &&
        !section.isDisable &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    ) {
      const isSeatReservationCompleted =
        takeAdmissionCompletionPercentage >= 100 ||
        directCampusVisitSrCompletionPercentage >= 100;
      const isEnrollmentAccessible = isDirectFlowUser
        ? natSlotSelectionCompletionPercentage >= 100 &&
          isSeatReservationCompleted
        : isSeatReservationCompleted;

      if (
        natSlotSelectionCompletionPercentage < 100 &&
        !isEnrollmentAccessible
      ) {
        section.isHidden = true;
      } else if (!isEnrollmentAccessible) {
        section.isDisable = true;
      } else {
        section.isHidden = false;
        section.isDisable = false;
      }
      if (
        !section.isHidden &&
        !section.isDisable &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    }

    sectionDetailsInfoTabsDetailsArray.push(section);
  }

  //hide personal details section if nat section is completed
  if (natSlotSelectionCompletionPercentage >= 100) {
    const personalDetailsSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) => section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS
    );
    const applicationFeeSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    );
    const directCampusVisitSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    );
    const directCampusVisitSRSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    );

    if (personalDetailsSection) {
      personalDetailsSection.isHidden = true;
    }

    if (applicationFeeSection) {
      applicationFeeSection.isHidden = true;
    }

    if (directCampusVisitSection) {
      directCampusVisitSection.isHidden = true;
    }

    if (directCampusVisitSRSection) {
      directCampusVisitSRSection.isHidden = false;
    }
  }

  const natSlotSelectionSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) => section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  );

  const shouldApplyPostAdmissionRules =
    natSlotSelectionCompletionPercentage >= 100 &&
    takeAdmissionCompletionPercentage >= 100;

  if (shouldApplyPostAdmissionRules) {
    applySectionVisibilityOverrides({
      hiddenSections: postAdmissionHiddenSections
    });
  }

  if (
    shouldApplyPostAdmissionRules &&
    selectedSectionTabId === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  ) {
    selectedSectionTabId = '';
    selectedSectionCompletionPercentage = 0;
  }

  if (natSlotSelectionSection?.subSection?.length > 2) {
    natSlotSelectionSection.subSection[2] > 0 &&
      natSlotSelectionSection.subSection[2].completionPercentage < 100 &&
      ((natSlotSelectionSection.subSection[2].completionPercentage = 99),
      (selectedSectionTabId = ApplicationSectionNameEnums.NAT_SLOT_SELECTION));
  }

  const shouldHideSectionOnCompletionOfSR =
    getShouldHideSectionOnCompletionOfSR(sectionDetailsInfoTabsConfig);

  if (shouldHideSectionOnCompletionOfSR) {
    applySectionVisibilityOverrides({
      visibleSections: visibleSectionsOnCompletionOfSR
    });

    const takeAdmissionSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) => section.id === ApplicationSectionNameEnums.TAKE_ADMISSION
    );
    if (takeAdmissionSection) {
      takeAdmissionSection.isHidden = false;
    }

    const enrollmentSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    );

    if (enrollmentSection) {
      enrollmentSection.isHidden = false;
      selectedSectionTabId =
        ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
      selectedSectionCompletionPercentage =
        enrollmentSection.completionPercentage ?? 0;
      selectedSubSectionId = '';
    }
  }

  // After seat reservation is complete, land users on NIAT Enrollment Fee next
  // (loop order can leave selection on TAKE_ADMISSION even at 100%).
  const takeAdmissionSectionFinal = sectionDetailsInfoTabsDetailsArray.find(
    (s) => s.id === ApplicationSectionNameEnums.TAKE_ADMISSION
  );
  const enrollmentSectionFinal = sectionDetailsInfoTabsDetailsArray.find(
    (s) => s.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
  );

  if (
    (isDirectFlowUser
      ? natSlotSelectionCompletionPercentage >= 100 &&
        (takeAdmissionSectionFinal?.completionPercentage >= 100 ||
          directCampusVisitSrCompletionPercentage >= 100)
      : takeAdmissionSectionFinal?.completionPercentage >= 100 ||
        directCampusVisitSrCompletionPercentage >= 100) &&
    enrollmentSectionFinal &&
    !enrollmentSectionFinal.isHidden &&
    !enrollmentSectionFinal.isDisable
  ) {
    selectedSectionTabId = ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
    selectedSectionCompletionPercentage =
      enrollmentSectionFinal.completionPercentage ?? 0;
    selectedSubSectionId = '';
  }

  return {
    sectionDetailsInfoTabsDetailsArray,
    selectedSectionTabId,
    selectedSubSectionId,
    selectedSectionCompletionPercentage
  };
};

export const getSectionDetailsInfoTabsConfig = (
  sectionDetails: any,
  loggedInUserRole: string,
  applicationType?: string,
  templateApplicationType?: string,
  isDirectCampusVisitedFromField = false
): any => {
  const sectionDetailsInfoTabsConfig: any[] = [];
  sectionDetails?.forEach((eachSection) => {
    const sectionDetailsInfoTabsConfigItem = {
      title: eachSection.title,
      description: eachSection.description,
      id: eachSection.sectionNameEnum,
      isSelected: false,
      completionPercentage: eachSection.completionPercentage,
      isDisable: true,
      sectionType: eachSection.sectionType,
      subSection: [],
      isHidden: eachSection.isHidden,
      shouldShowSubSection: false,
      sectionCompletedAt: eachSection.sectionCompletedAt,
      firstVisitedDatetime: eachSection.firstVisitedDatetime ?? null,
      extraConfig: eachSection.extraConfig,
      sectionId: eachSection.sectionId
    };

    if (eachSection.parentSectionDetails) {
      const parentNameEnum = eachSection.parentSectionDetails.nameEnum;
      const parentSection = sectionDetailsInfoTabsConfig.find(
        (section) => section.id === parentNameEnum
      );

      if (parentSection) {
        parentSection.subSection.push(sectionDetailsInfoTabsConfigItem);
      } else {
        sectionDetailsInfoTabsConfig.push({
          title: eachSection.parentSectionDetails.title,
          description: eachSection.parentSectionDetails.description,
          id: parentNameEnum,
          isSelected: false,
          completionPercentage: 0,
          sectionType: eachSection.sectionType,
          isDisable: true,
          subSection: [sectionDetailsInfoTabsConfigItem],
          isHidden: eachSection.isHidden,
          shouldShowSubSection: false,
          sectionCompletedAt: eachSection.sectionCompletedAt,
          firstVisitedDatetime: eachSection.firstVisitedDatetime ?? null,
          extraConfig: eachSection.extraConfig,
          sectionId: eachSection.sectionId
        });
      }
    } else {
      sectionDetailsInfoTabsConfig.push(sectionDetailsInfoTabsConfigItem);
    }
  });
  return getSectionDetailsDisableInfoTabsConfig(
    sectionDetailsInfoTabsConfig,
    loggedInUserRole,
    undefined,
    applicationType,
    templateApplicationType,
    isDirectCampusVisitedFromField
  );
};
const APAndNonAPSectionTabIds = [
  ApplicationSectionNameEnums.WEBINAR,
  ApplicationSectionNameEnums.ONLINE_COUNSELLING,
  ApplicationSectionNameEnums.CLOSURE_SESSION_REGISTRATION,

  ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT,

  ApplicationSectionNameEnums.TAKE_ADMISSION
];

const isAPFlow = (sectionDetailsInfoTabsConfig: any[]): boolean => {
  return sectionDetailsInfoTabsConfig.some((section) => {
    return (
      section.id === ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
    );
  });
};
export const getSectionQuotationText = ({ niatApplicationMachineState }) => {
  if (niatApplicationMachineState) {
    const {
      context: {
        currentSelectedSectionTabId,
        sectionDetailsInfoTabsConfig,
        currentSelectedSubSectionId,
        currentSectionType
      }
    } = niatApplicationMachineState;
    const applicationFeeSection = sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    );

    if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.PERSONAL_DETAILS
    ) {
      return `<span>🥳 2,36,000+ students</span> like you across India took this step. Join them today!`;
    } else if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    ) {
      return `A <strong>90-Minute Test</strong> To Uncover Your True Potential!`;
    } else if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER
    ) {
      return `🥳 See and feel the <span>NIAT's ecosystem.</span>`;
    } else if (
      applicationFeeSection?.completionPercentage >= 100 &&
      currentSelectedSubSectionId ===
        ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
    ) {
      return `🔥 Slots filling fast! Book your preferred date and time for the test slot now.`;
    } else if (
      currentSelectedSubSectionId === ApplicationSectionNameEnums.TOPIN_EXAM
    ) {
      if (
        currentSectionType === ApplicationSectionNameEnums.EXAM_SLOT_SUCCESS ||
        currentSectionType ===
          ApplicationSectionNameEnums.RESCHEDULE_SLOT_SUCCESS
      ) {
        return `Don't miss giving your NAT! Download the <strong><a id="nat-guide-link-nudge" href="${NAT_GUIDE_URL}" target="_blank">NAT Guide</a></strong> for more insights.`;
      } else if (
        currentSectionType === ApplicationSectionNameEnums.RESCHEDULE_SLOT
      ) {
        return `Reschedule Now! Take the NAT at your convenience.`;
      } else {
        return `NAT report helps you understand where you stand in the core abilities needed for a tech career.`;
      }
    } else if (
      applicationFeeSection?.completionPercentage >= 100 &&
      currentSelectedSubSectionId ===
        ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_DETAILS
    ) {
      return `Success might just be one attempt away. Reapply for NAT now!`;
    } else if (
      currentSelectedSubSectionId ===
      ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
    ) {
      if (
        currentSectionType ===
        ApplicationSectionNameEnums.RETAKE_RESCHEDULE_SLOT
      ) {
        return `Reschedule Now! Take the NAT at your convenience.`;
      } else if (
        currentSectionType ===
          ApplicationSectionNameEnums.RETAKE_RESCHEDULE_SLOT_SUCCESS ||
        currentSectionType ===
          ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_SUCCESS
      ) {
        return `Don't miss giving your NAT! Download the <strong><a id="nat-guide-link-nudge" href="${NAT_GUIDE_URL}" target="_blank">NAT Guide</a></strong> for more insights.`;
      } else {
        return `NAT report helps you understand where you stand in the core abilities needed for a tech career.`;
      }
    } else if (APAndNonAPSectionTabIds.includes(currentSelectedSectionTabId)) {
      for (const section of sectionDetailsInfoTabsConfig) {
        if (section.completionPercentage < 100) {
          if (
            section.id ===
            ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
          ) {
            return `Schedule your <span>Experience Center visit</span> to complete the in-person evaluation process.`;
          } else if (
            section.id === ApplicationSectionNameEnums.ONLINE_COUNSELLING
          ) {
            const onlineCounsellingNudgeText1 = `Book online counseling to discuss your results and personalized learning path.`;
            const onlineCounsellingNudgeText2 = `Online counseling helps you clarify course options, eligibility, and next actions.`;
            return section.completionPercentage < 100 &&
              section.completionPercentage >= 50
              ? onlineCounsellingNudgeText2
              : onlineCounsellingNudgeText1;
          } else if (section.id === ApplicationSectionNameEnums.WEBINAR) {
            const liveSessionNudgeText1 = `Register for the <span>live session</span> to understand the NIAT process and next steps clearly.`;
            const liveSessionNudgeText2 = `Join the <span>live session</span> to get guidance on counselling and seat reservation.`;
            return section.completionPercentage < 100 &&
              section.completionPercentage >= 50
              ? liveSessionNudgeText2
              : liveSessionNudgeText1;
          } else if (
            section.id === ApplicationSectionNameEnums.TAKE_ADMISSION
          ) {
            const APFlowNudgeText = `Reserve your <span>NIAT seat</span> to confirm your admission after completing the Experience Center visit.<br />Seat availability is limited and subject to real-time confirmation.`;
            const NonAPFlowNudgeText = `Reserve your <span>NIAT seat</span> to secure your admission and lock in your learning journey.<br />Seats are limited and allocated on a first-come, first-served basis.`;
            return isAPFlow(sectionDetailsInfoTabsConfig)
              ? APFlowNudgeText
              : NonAPFlowNudgeText;
          }
        }
      }
    }
  }
  return `🎯 The journey to becoming a <span>Future Tech Leader</span> starts here!`;
};
